import { useEffect } from 'react';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  useQuizMetaContext,
  useQuizResults,
  useSocketIoConnection,
  QuizConfigProvider,
  useQuizStatus,
} from '@mentimeter/quiz';
import type { QuizResultData } from '@mentimeter/core-hooks';
import { ClientErrorCodes, GameStateEnums } from '@mentimeter/quiz-types';
import { Loading } from '../../shared/components/quiz/loading';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Error } from '../../shared/components/quiz/error';
import { LivePresentation } from './LivePresentation';
import { FollowerPresentation } from './FollowerPresentation';
import { PublicPresentation } from './PublicPresentation';

const Live = () => {
  const { useQuestion, useCurrentSession } = usePresentationContext();
  const {
    question,
    question_styled,
    image,
    choices,
    countdown_to_vote,
    time_based_scoring,
    title_meta,
    question_description,
    layout,
  } = useQuestion();
  const { data, error, mutate } = useQuizResults<QuizResultData>();
  const { currentSessionId: session } = useCurrentSession();
  const { connection } = useSocketIoConnection();
  const { timeOffset } = useQuizStatus(connection);
  const { follower } = useQuizMetaContext();

  const shouldShowLegacyImage = layout?.type === 'default';

  // We can have total scores, but we need to determine for this question
  const hasResultsForQuestion = Boolean(
    data?.some((r) => Boolean(r.responses)),
  );

  // Invalidate results when session is updated
  useEffect(() => {
    return () => void mutate(undefined);
  }, [session, mutate]);

  if (
    !data ||
    !connection ||
    session === undefined ||
    timeOffset === undefined
  ) {
    return (
      <CenteringContainer>
        <Loading />
      </CenteringContainer>
    );
  }

  if (error) {
    return (
      <CenteringContainer>
        <Error errorCode={ClientErrorCodes.RESULTS_FETCH} />
      </CenteringContainer>
    );
  }

  if (follower) {
    return (
      <FollowerPresentation
        activeSession={session}
        imageUrl={image?.presets.medium.url}
        timeBasedScoring={time_based_scoring}
        question={question}
        questionStyled={question_styled}
        meta={title_meta}
        description={question_description}
        choices={choices}
        shouldShowLegacyImage={shouldShowLegacyImage}
        layout={layout}
      />
    );
  }

  return (
    <LivePresentation
      activeSession={session}
      initialGameState={
        hasResultsForQuestion ? GameStateEnums.RESULT : GameStateEnums.LOBBY
      }
      imageUrl={image?.presets.medium.url}
      timeBasedScoring={time_based_scoring}
      question={question}
      questionStyled={question_styled}
      meta={title_meta}
      description={question_description}
      countdownToVote={countdown_to_vote}
      choices={choices}
      shouldShowLegacyImage={shouldShowLegacyImage}
      layout={layout}
    />
  );
};

export function QuizChoicesPresentation() {
  const { useQuestion, useVotingInstructions, useConfig } =
    usePresentationContext();
  const { voteKey } = useVotingInstructions();
  const { mode } = useConfig();
  const {
    public_key,
    question,
    question_styled,
    title_meta,
    question_description,
    id,
    choices,
    admin_key,
    layout,
  } = useQuestion();

  return (
    <QuizConfigProvider
      questionPublicKey={public_key}
      questionAdminKey={admin_key}
      voteKey={voteKey}
    >
      {mode !== 'live' ? (
        <PublicPresentation
          question={question}
          questionStyled={question_styled}
          meta={title_meta}
          description={question_description}
          id={id}
          choices={choices}
          layout={layout}
        />
      ) : (
        <Live />
      )}
    </QuizConfigProvider>
  );
}
